import { useEffect, useState } from "react";
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine } from "recharts";
import { ChevronRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { inr, pct } from "../lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loading, PageHeader, StatCard, StatusBadge, SectionTitle, Empty } from "./kit";

export default function NetworkDashboard() {
  const { month, setStoreId, basis, range, stores } = useAuth();
  const [d, setD] = useState(null);
  const [region, setRegion] = useState("all");
  const [mgr, setMgr] = useState("all");
  const [managers, setManagers] = useState([]);
  useEffect(() => { api.get("/users", { params: { role: "manager" } }).then((r) => setManagers(r.data)); }, []);
  useEffect(() => {
    setD(null);
    api.get("/performance/network", { params: { month, basis, start: range?.start, end: range?.end,
      region: region !== "all" ? region : undefined, manager_id: mgr !== "all" ? mgr : undefined } }).then((r) => setD(r.data));
  }, [month, basis, range, region, mgr]);
  const regions = [...new Set(stores.map((x) => x.region).filter(Boolean))];
  if (!d) return <Loading />;
  const s = d.summary;
  const chart = d.stores.map((x) => ({ name: x.name, pace: x.sales.pace || 0, status: x.status }));
  const color = { ahead: "#15803D", on_track: "#0369A1", behind: "#C2410C", critical: "#B91C1C" };

  return (
    <div className="space-y-10">
      <PageHeader eyebrow={`as of ${d.as_of}`} title="BLUESTONE RETAIL PERFORMANCE" testid="network-title"
        right={<div className="flex gap-2">
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger className="w-40 h-10" data-testid="network-region-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All regions</SelectItem>{regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={mgr} onValueChange={setMgr}>
            <SelectTrigger className="w-44 h-10" data-testid="network-manager-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All managers</SelectItem>{managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard label={basis === "ex_coin" ? "Net Sales w/o Coin" : "Net Sales"} value={inr(s.actual)} sub={`of ${inr(s.target)} target`} testid="network-total-sales" accent />
        <StatCard label="Expected Sales" value={inr(s.expected)} sub={`Gap ${inr(s.gap)}`} testid="network-expected-sales" />
        <StatCard label="Achievement" value={pct(s.achievement)} sub={`${pct(s.pace, 0)} of expected pace`} testid="network-achievement" />
        <StatCard label="Total Stores" value={s.stores} sub="Active stores" testid="network-total-stores" />
        <StatCard label="Period Target" value={inr(s.target)} testid="network-sales-target" />
        <StatCard label="Total GMS" value={inr(s.gms_actual)} sub={`of ${inr(s.gms_target)}`} testid="network-total-gms" />
        <StatCard label="GMS Achievement" value={pct(s.gms_achievement)} testid="network-gms-achievement" />
        <StatCard label="Gross Sales" value={inr(s.gross_sales)} sub={`Returns -${inr(s.return_sales)}`} testid="network-gross-sales" />
        <StatCard label="Gold Coin" value={inr(s.gold_coin)} sub={`W/o coin ${inr(s.sales_ex_coin)}`} testid="network-gold-coin" />
      </div>

      <section>
        <SectionTitle eyebrow="Store-level" title="Store Performance" />
        <div className="bs-card overflow-x-auto">
          <table className="w-full text-left" data-testid="network-store-table">
            <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <tr>{["Store", "Target", "Actual", "Expected", "Gap", "Achievement", "Status", ""].map((h) => <th key={h} className={`px-5 py-4 font-semibold ${h && h !== "Store" && h !== "Status" ? "text-right" : ""}`}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {d.stores.map((r) => (
                <tr key={r.id} onClick={() => setStoreId(r.id)} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" data-testid={`network-store-row-${r.id}`}>
                  <td className="px-5 py-5"><div className="font-semibold text-[#011E38] text-base">{r.name}</div><div className="text-xs text-slate-500">{[r.city, r.region].filter(Boolean).join(" · ")}</div></td>
                  <td className="px-5 num text-right text-lg">{inr(r.sales.target)}</td>
                  <td className="px-5 num text-right text-lg font-bold text-[#011E38]">{inr(r.sales.actual)}</td>
                  <td className="px-5 num text-right text-lg">{inr(r.sales.expected)}</td>
                  <td className={`px-5 num text-right text-lg font-semibold ${r.sales.gap < 0 ? "text-orange-700" : "text-green-700"}`}>{inr(r.sales.gap)}</td>
                  <td className="px-5 num text-right text-lg">{pct(r.sales.achievement)}</td>
                  <td className="px-5"><StatusBadge status={r.status} /></td>
                  <td className="px-5 text-slate-400"><ChevronRight className="w-4 h-4" /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!d.stores.length && <Empty>No active stores yet.</Empty>}
        </div>
      </section>

      {chart.length > 0 && (
        <section className="bs-card p-6">
          <SectionTitle eyebrow="Pace vs expected" title="Store comparison" />
          <div style={{ height: Math.max(160, chart.length * 56) }}>
            <ResponsiveContainer>
              <BarChart data={chart} layout="vertical" margin={{ left: 20, right: 30 }}>
                <XAxis type="number" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" width={170} tick={{ fontSize: 13, fill: "#011E38" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => `${v.toFixed(1)}%`} />
                <ReferenceLine x={100} stroke="#C5A059" strokeDasharray="4 4" />
                <Bar dataKey="pace" radius={[0, 8, 8, 0]} barSize={22}>
                  {chart.map((c) => <Cell key={c.name} fill={color[c.status] || "#011E38"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}
    </div>
  );
}
